import { Button } from '@fluentui/react-components';
import { daysUntilDeadline, offerAcceptByText, rejectionSentenceFor, type OfferRecord, type Person, type StepInstance } from '@vigeo/shared';
import { WithdrawOfferButton } from '@/features/hr-workflow';
import { day, dayAndTime, reachedStage } from '@/features/offers';
import { OfferSummary } from './OfferSummary.js';
import { HandOverLinkButton, HandOverLinkDialog, useHandOverLink } from './HandOverLink.js';
import { byLabel, useSignedInUserId } from '../actors.js';

const first = (person: Person) => person.name.preferredName || person.name.givenName;
const fullName = (person: Person) => `${person.name.givenName} ${person.name.familyName}`;
const dueIn = (record: OfferRecord, now: string) => {
  const days = daysUntilDeadline(record.terms.acceptanceDeadline, now);
  return days <= 0 ? 'due today' : `due in ${days} ${days === 1 ? 'day' : 'days'}`;
};

/** Draft saved — 970:13378. Review offer is the one primary action; it opens 1.3. */
export function DraftSaved({ person, record, canEdit, busy, onEdit, onReview }: {
  readonly person: Person; readonly record: OfferRecord; readonly canEdit: boolean; readonly busy: boolean;
  readonly onEdit: () => void; readonly onReview: () => void;
}) {
  const me = useSignedInUserId();
  return <OfferSummary
    person={person} record={record}
    badge={{ label: 'Draft', tone: 'neutral' }}
    title="Draft saved"
    lede="Not reviewed yet. It can’t be sent until you review it."
    reached={reachedStage(record.status, false)}
    offerLabel="Offer" offerHint={`Saved ${day(record.updatedAt)}${byLabel(record.updatedBy, me)}.`}
    actions={canEdit ? <>
      <Button appearance="subtle" disabled={busy} onClick={onEdit}>Edit draft</Button>
      <Button appearance="primary" disabled={busy} onClick={onReview}>Review offer</Button>
    </> : null}
  />;
}

/**
 * Sent, waiting for the candidate — 970:13595.
 *
 * "Resend link" is not drawn here yet: it needs a decision on which endpoint it calls (the HR
 * `portal-link` issue route revokes the link the candidate is holding). Tracked on #440.
 */
export function SentWaiting({ person, record, step, canEdit, busy, now, onWithdraw }: {
  readonly person: Person; readonly record: OfferRecord; readonly step: StepInstance | null;
  readonly canEdit: boolean; readonly busy: boolean; readonly now: string; readonly onWithdraw: () => void;
}) {
  const me = useSignedInUserId();
  const link = useHandOverLink(person, true);
  const sentAt = step?.sentAt ?? record.updatedAt;
  return <>
    <OfferSummary
      person={person} record={record}
      badge={{ label: 'Out for signature', tone: 'warning' }}
      title={`Waiting for ${first(person)} to sign`}
      lede={`Accept by ${dayAndTime(record.terms.acceptanceDeadline)} · ${dueIn(record, now)}. Sent ${day(sentAt)}.`}
      reached={reachedStage('issued', false)}
      offerLabel="Offer" offerHint={`Sent ${day(sentAt)}${byLabel(step?.sentBy ?? record.updatedBy, me)}. Locked.`}
      actions={canEdit ? <>
        <HandOverLinkButton handOver={link} appearance="subtle" />
        <WithdrawOfferButton candidateName={fullName(person)} disabled={busy} onConfirm={onWithdraw} />
      </> : null}
    />
    {canEdit ? <HandOverLinkDialog handOver={link} firstName={first(person)} /> : null}
  </>;
}

/** Sent back, waiting to sign again — 971:13847. The reason is the sentence the candidate reads. */
export function SentBack({ person, record, step, canEdit, busy, now, onWithdraw }: {
  readonly person: Person; readonly record: OfferRecord; readonly step: StepInstance;
  readonly canEdit: boolean; readonly busy: boolean; readonly now: string; readonly onWithdraw: () => void;
}) {
  const me = useSignedInUserId();
  const reason = rejectionSentenceFor(step.rejectionReason);
  const sentBackAt = step.reviewedAt ?? record.updatedAt;
  return <OfferSummary
    person={person} record={record}
    badge={{ label: 'Sent back', tone: 'warning' }}
    title={`Waiting for ${first(person)} to sign again`}
    lede={`Sent back ${day(sentBackAt)}. Accept by ${offerAcceptByText(record.terms.acceptanceDeadline)} · ${dueIn(record, now)}.`}
    reached={reachedStage('issued', true)}
    offerLabel="Offer" offerHint={`Sent back ${day(sentBackAt)}${byLabel(step.reviewedBy, me)}. Locked.`}
    lead={reason === null ? [] : [{ label: 'Reason', value: reason }]}
    actions={canEdit ? <WithdrawOfferButton candidateName={fullName(person)} disabled={busy} onConfirm={onWithdraw} /> : null}
  />;
}

/** Offer expired — 971:14266. Read-only; a new draft starts from these details. */
export function OfferExpired({ person, record, canEdit, busy, onDraftNew }: {
  readonly person: Person; readonly record: OfferRecord; readonly canEdit: boolean; readonly busy: boolean; readonly onDraftNew: () => void;
}) {
  return <OfferSummary
    person={person} record={record}
    badge={{ label: 'Expired', tone: 'critical' }}
    title="Offer expired"
    lede={`Accept by ${dayAndTime(record.terms.acceptanceDeadline)} passed without a signature. ${first(person)} can’t sign it now.`}
    offerLabel="Expired offer" offerHint="Read-only, kept on the audit trail. A new draft starts from these details."
    actions={canEdit ? <Button appearance="primary" disabled={busy} onClick={onDraftNew}>Draft a new offer</Button> : null}
  />;
}
